import sessionStrageUtils from '@/lib/sessionStrage/session-strage-utils'
import { defineObject } from '@/lib/define/use-define'
import format from '@/lib/formatUtils/format'
import {
  handleCurrentJapaneseCalendar,
  handleCheckDiffCldObjAffiliationAndAchievementsAffiliationByVisit,
  handleSetVisitAchivementsVisitRemarks,
} from '@/lib/achievements/utils'
import moment from 'moment'
import {
  handleJapaneseCalendar,
  handleShapeUpdateReBillingCldParam,
  handleShapeUpdateReBillingOfficeParam,
  useSummaryValid,
} from '@/lib/billing/utils'
import {
  calculationOfUsageTime,
  handleCheckDupDayAchivementsArray,
  handleCheckCountConsultationAddition,
  handleCheckCountOrganizationAddition,
  handleCheckKasanNyuyoku,
  handleCheckKasanKazokuShien,
  handleCheckKasanKosodateSupport,
  handleCheckKasanSenmontekiJisshi,
  handleCheckKasanJigyoshokan,
  handleCheckKasanJiritsuSupport,
  handleCheckKasanIkoShien,
  handleCheckKasanShuchu,
  handleCheckCalculationTimeOverOfProvisionFormOpen,
  handleCheckCalculationTimeOverOfProvisionFormClosed,
  handleCheckAbsence,
  handleCheckVisitOrganizationAddition,
  handleCheckVisitKasanKazokuShien,
  handleCheckVisitKasanTashugyo,
  handleCheckDiffCldObjAffiliationAndAchievementsAffiliation,
} from '@/lib/achievements/revision_2024/utils'
import {
  ResponseAchievementsType,
  ResponseVisitAchievementsType,
  cldBookForAchievementsType,
  DisplayVisitAchievementsType,
} from '@/@types/achievements/revision_2024'
import { handleSetInvoicePrice } from '@/lib/parents/utils'
import { handleCheckBillingOfficeDataForRevision2024 } from '@/lib/office/utils'

const JIHATSU = '1'
const HOUDAY = '0'
const HOHOU = '2'

export const getSessionStorageReBillingDate = () => {
  const reBillingData = sessionStrageUtils.handleGetSessionStorage(
    'reBillingData'
  )

  const formatDate = moment(reBillingData.date, 'YYYY/MM').format('YYYY/MM')

  const formatDateArray = formatDate.split('/')

  return {
    year: formatDateArray[0],
    month: formatDateArray[1],
  }
}

export const getSessionStorageChildDataByCldId = (cld: string) => {
  const reBillingData = sessionStrageUtils.handleGetSessionStorage(
    'reBillingData'
  )

  let cldObj: any = {}

  if (
    reBillingData.child &&
    reBillingData.child.find((child: any) => child.id === cld)
  ) {
    cldObj = reBillingData.child.find((child: any) => child.id === cld)
  }

  return cldObj
}

export const getSessionStorageChildData = () => {
  const reBillingData = sessionStrageUtils.handleGetSessionStorage(
    'reBillingData'
  )

  return reBillingData.child
}

export const getSessionStorageOfficeData = () => {
  const reBillingData = sessionStrageUtils.handleGetSessionStorage(
    'reBillingData'
  )

  return reBillingData.office
}

export const getSessionStorageAchievementsDataByCldId = (cldId: string) => {
  const reBillingData = sessionStrageUtils.handleGetSessionStorage(
    'reBillingData'
  )

  const achievementsDataByCldId = reBillingData.achievements.filter(
    (achievements: any) => achievements.cld_id === cldId
  )

  return achievementsDataByCldId
}

export const getSessionStorageVisitAchievementsDataByCldId = (
  cldId: string
) => {
  const reBillingData = sessionStrageUtils.handleGetSessionStorage(
    'reBillingData'
  )

  const achievementsDataByCldId = reBillingData.visit_achievements.filter(
    (visitAchievements: any) => visitAchievements.cld_id === cldId
  )

  return achievementsDataByCldId
}

export const getSessionStorageAchievementsDataByCldIdAndAffiliation = (
  cldId: string,
  affiliation: string
) => {
  const reBillingData = sessionStrageUtils.handleGetSessionStorage(
    'reBillingData'
  )

  const achievementsDataByCldIdAndAffiliation = reBillingData.achievements.filter(
    (achievements: any) =>
      achievements.cld_id === cldId && achievements.affiliation === affiliation
  )

  return achievementsDataByCldIdAndAffiliation
}

export const getSessionStorageAchievementsDataByCldIdAndDate = (
  cldId: string,
  year: number | string,
  month: number | string,
  day: number | string,
  targetAffiliation: string
) => {
  const reBillingData = sessionStrageUtils.handleGetSessionStorage(
    'reBillingData'
  )

  const achievementsDataByCldIdAndDate = reBillingData.achievements.find(
    (achievements: any) =>
      achievements.cld_id === cldId &&
      Number(achievements.year) === Number(year) &&
      Number(achievements.month) === Number(month) &&
      Number(achievements.day) === Number(day) &&
      achievements.affiliation === targetAffiliation
  )

  return achievementsDataByCldIdAndDate
}

export const getSessionStorageVisitAchievementsDataByCldIdAndDate = (
  cldId: string,
  year: number | string,
  month: number | string,
  day: number | string
) => {
  const reBillingData = sessionStrageUtils.handleGetSessionStorage(
    'reBillingData'
  )

  const visitAchievementsDataByCldIdAndDate = reBillingData.visit_achievements.find(
    (achievements: any) =>
      achievements.cld_id === cldId &&
      Number(achievements.year) === Number(year) &&
      Number(achievements.month) === Number(month) &&
      Number(achievements.day) === Number(day)
  )

  return visitAchievementsDataByCldIdAndDate
}

export const getSessionStorageAchievementsData = () => {
  const reBillingData = sessionStrageUtils.handleGetSessionStorage(
    'reBillingData'
  )

  return reBillingData.achievements
}

export const getSessionStorageVisitAchievementsData = () => {
  const reBillingData = sessionStrageUtils.handleGetSessionStorage(
    'reBillingData'
  )

  return reBillingData.visit_achievements
}

export const getSessionStoragCldReBillingData = () => {
  const reBillingData = sessionStrageUtils.handleGetSessionStorage(
    'reBillingData'
  )

  return reBillingData.billing
}

export const getSessionStoragCldReBillingDataByCld = (cldId: string) => {
  const reBillingData = sessionStrageUtils.handleGetSessionStorage(
    'reBillingData'
  )

  const billing = reBillingData.billing.find(
    (cldBilling: any) => cldBilling.cld_id === cldId
  )

  return billing
}

export const isCldReBillingDataUpdated = (cldId: string) => {
  const reBillingData = sessionStrageUtils.handleGetSessionStorage(
    'reBillingData'
  )

  let res = false

  res = reBillingData.billing.some(
    (cldBilling: any) => cldBilling.cld_id === cldId && cldBilling.isUpdate
  )

  return res
}

export const getSessionStoragCldReBillingDataByOtherOfficeNumber = (
  otherOfficeNumber: string,
  billing: any[]
) => {
  const filterMunicipalitiesNoBilliingArray = billing.filter(
    (billingObj: any) =>
      billingObj.details.master.officeNumber === otherOfficeNumber
  )
  return filterMunicipalitiesNoBilliingArray
}

export const getSessionStorageSummaryData = () => {
  const reBillingData = sessionStrageUtils.handleGetSessionStorage(
    'reBillingData'
  )

  return reBillingData.summary
}

export const getSessionStorageInvoiceData = () => {
  const reBillingData = sessionStrageUtils.handleGetSessionStorage(
    'reBillingData'
  )

  return reBillingData.invoice
}

export const getSessionStoragReBillingData = () => {
  const reBillingData = sessionStrageUtils.handleGetSessionStorage(
    'reBillingData'
  )

  return reBillingData
}

export const handleWindowAlert = (event: any) => {
  event.returnValue = '編集中のものは保存されませんが、よろしいですか？'
}

export const handleIsOfficeUpdated = (office: any) => {
  let res = false

  if (office && office.is_update) {
    res = true
  }

  return res
}

export const handleIsChildCheck = (cldObj: any, office: any) => {
  const {
    handleIsDiffCldObjAffiliationAndOfficeAffiliationCld,
  } = useSummaryValid()

  try {
    let res = false

    if (!cldObj) throw new Error('')

    const checkKeyArray1 = []

    if (cldObj.affiliation === '0') {
      checkKeyArray1.push('severe_child_flg')
      checkKeyArray1.push('medical_care_child_flg')
    }

    if (cldObj.affiliation === '1') {
      checkKeyArray1.push('jihatsu_kasan_preschoolers_flg')
      checkKeyArray1.push('jihatsu_kasan_severely_disabled')
      checkKeyArray1.push('jihatsu_medical_care_child_flg')
    }

    res = checkKeyArray1.every(
      (checkKey: string) =>
        cldObj[checkKey] !== null &&
        cldObj[checkKey] !== undefined &&
        cldObj[checkKey] !== ''
    )

    if (!res) throw new Error('')

    const checkKeyArray2 = [
      'last_name',
      'first_name',
      'last_kana',
      'first_kana',
      'parents_last_name1',
      'parents_first_name1',
      'parents_last_kana1',
      'parents_first_kana1',
      'beneficiary_id',
      'city_number',
      'ub_upper_limit_monthly_amount',
      'parents_kana',
      'child_kana',
    ]

    if (cldObj.affiliation === '0' || cldObj.affiliation === '1') {
      checkKeyArray2.push(
        ...[
          'affiliation',
          'contract_amount',
          'start_using_date',
          'beneficiary_entry_field_no',
          'contract_date',
        ]
      )
    }

    if (cldObj.affiliation === '2' || cldObj.visit_child_flg === '1') {
      checkKeyArray2.push(
        ...[
          'visit_contract_amount',
          'visit_start_using_date',
          'visit_beneficiary_entry_field_no',
          'visit_contract_date',
        ]
      )
    }

    if (cldObj.affiliation === '' && cldObj.visit_child_flg === '0') {
      checkKeyArray2.push(...['affiliation'])
    }

    res = checkKeyArray2.every(
      (checkKey: string) =>
        cldObj[checkKey] !== null &&
        cldObj[checkKey] !== undefined &&
        cldObj[checkKey] !== ''
    )

    if (!res) throw new Error('')

    if (
      cldObj.upper_limit['0'] &&
      Object.keys(cldObj.upper_limit['0']).length !== 0 &&
      Object.keys(cldObj.upper_limit['0']).some(
        (key: string) =>
          key === 'office_number' &&
          String(cldObj.upper_limit['0'][key]) === String(office.office_number)
      ) &&
      !cldObj.creation_category
    ) {
      res = false
    }

    if (
      cldObj.upperLimitAdmin === '自社' &&
      (Object.keys(cldObj.office_array['0']).length === 0 ||
      Object.keys(cldObj.office_array['0']).some(
        (key: string) =>
          key === 'office_number' &&
          String(cldObj.office_array['0'][key]) === String(office.office_number)
      ))
    ) {
      res = false
    }

    console.log(cldObj, 'cldObj')

    if (cldObj.affiliation === '0') {
      if (
        cldObj['kasan_support1'] === '1' &&
        (cldObj['kasan_support1_detail'] === '' || cldObj['kasan_support1_detail'] === '0')
      ) {
        res = false
      }
      if (
        cldObj['kasan_tsusho_jiritsu'] === '1' &&
        (cldObj['kasan_tsusho_jiritsu_start_date'] === '' || cldObj['kasan_tsusho_jiritsu_start_date'] === undefined)
      ) {
        res = false
      }
      if (
        (cldObj['kasan_kyodo_kodo'] === '1' || cldObj['kasan_kyodo_kodo'] === '2') &&
        (cldObj['kasan_kyodo_kodo_start_date'] === '' || cldObj['kasan_kyodo_kodo_start_date'] === undefined)
      ) {
        res = false
      }
    }

    if (cldObj.affiliation === '1') {
      if (
        cldObj['jihatsu_kasan_strength'] === '1' &&
        (cldObj['jihatsu_kasan_kyodo_kodo_start_date'] === '' || cldObj['jihatsu_kasan_kyodo_kodo_start_date'] === undefined)
      ) {
        res = false
      }
    }

    if (!res) throw new Error('')

    res = !handleIsDiffCldObjAffiliationAndOfficeAffiliationCld([cldObj])

    if (!res) throw new Error('')

    if(cldObj.isDiffJihatsuKasanPreschoolers) throw new Error('')

    return res
  } catch (error) {
    return false
  }
}

export const handleShortWareki = (date: string) => {
  const formatDate = new Intl.DateTimeFormat('ja-JP-u-ca-japanese', {
    era: 'narrow',
  }).format(new Date(date))
  return formatDate
}

export const handleShapeBilling = (billingArray: any) => {
  billingArray.forEach((billingObj: any) => {
    if (billingObj.upperLimit.master.date) {
      billingObj.upperLimit.master.date = moment(
        billingObj.upperLimit.master.date,
        'YYYY/MM'
      ).format('YYYY/MM')
    }

    if (billingObj.details.master.date) {
      billingObj.details.master.date = moment(
        billingObj.details.master.date,
        'YYYY/MM'
      ).format('YYYY/MM')
    }
  })

  return billingArray
}

const isInvalidDate = (date: Date) => Number.isNaN(date.getTime())

export const handleShapeChild = (childArray: any) => {
  const dateKeyArray = [
    'contract_date',
    'contract_end_date',
    'start_using_date',
    'end_using_date',
    'visit_contract_date',
    'visit_contract_end_date',
    'visit_start_using_date',
    'visit_end_using_date',
  ]

  const intKeyPrimitiveKeyArray = [
    'contract_amount',
    'beneficiary_entry_field_no',
    'visit_contract_amount',
    'visit_beneficiary_entry_field_no',
  ]

  const intKeyObjKeyArray = ['contract_amount', 'column_number']

  childArray.forEach((cld: any) => {
    dateKeyArray.forEach((key: string) => {
      if (isInvalidDate(new Date(cld[key]))) {
        cld[key] = ''
      }
    })

    intKeyPrimitiveKeyArray.forEach((key: string) => {
      if (cld[key]) {
        cld[key] = Number(cld[key])
      }
    })

    if (Object.keys(cld.upper_limit).length !== 0) {
      intKeyObjKeyArray.forEach((key: string) => {
        if (cld.upper_limit['0'][key]) {
          cld.upper_limit['0'][key] = Number(cld.upper_limit['0'][key])
        }
      })
    }

    if (Object.keys(cld.office_array).length !== 0) {
      Object.keys(cld.office_array).forEach((officeKey: string) => {
        intKeyObjKeyArray.forEach((key: string) => {
          if (cld.office_array[officeKey][key]) {
            cld.office_array[officeKey][key] = Number(
              cld.office_array[officeKey][key]
            )
          }
        })
      })
    }
  })

  return childArray
}

export const handleShapeVisitAchievements = (visitAchievementsArray: any[]) => {
  for (let index = 0; index < visitAchievementsArray.length; index++) {
    const taregetTimeKeyReg = new RegExp(
      'StartHour|StartMinute|EndHour|EndMinute'
    )

    let checkKasanKeyArray = defineObject.ACHIVEMENTS_REVISION_2024_KASAN_KEY_ARRAY.filter(
      (kasanKey: string) =>
        !kasanKey.match(taregetTimeKeyReg) && kasanKey.match('homeAddition')
    )

    checkKasanKeyArray = checkKasanKeyArray.map((kasanKey: string) =>
      format.snakeCase(kasanKey)
    )

    const comeAndReturnKeyArray = [
      'come_time_hour',
      'come_time_minute',
      'return_time_hour',
      'return_time_minute',
      'visit_kasan_kazoku_shien_start_hour',
      'visit_kasan_kazoku_shien_start_minute',
      'visit_kasan_kazoku_shien_end_hour',
      'visit_kasan_kazoku_shien_end_minute',
    ]

    comeAndReturnKeyArray.forEach((key: string) => {
      if (visitAchievementsArray[index][key]) {
        visitAchievementsArray[index][key] = (
          '00' + visitAchievementsArray[index][key]
        ).slice(-2)
      } else {
        visitAchievementsArray[index][key] = '00'
      }
    })

    const intKeyPrimitiveKeyArray = ['flg', 'year', 'month', 'day']

    intKeyPrimitiveKeyArray.forEach((key: string) => {
      if (
        visitAchievementsArray[index][key] !== undefined &&
        visitAchievementsArray[index][key] !== null &&
        visitAchievementsArray[index][key] !== ''
      ) {
        visitAchievementsArray[index][key] = Number(
          visitAchievementsArray[index][key]
        )
      }
    })

    checkKasanKeyArray.forEach((key: string) => {
      if (
        visitAchievementsArray[index][key] === '0' ||
        visitAchievementsArray[index][key] === 0
      ) {
        visitAchievementsArray[index][key + '_start_hour'] = '00'
        visitAchievementsArray[index][key + '_start_minute'] = '00'
        visitAchievementsArray[index][key + '_end_hour'] = '00'
        visitAchievementsArray[index][key + '_end_minute'] = '00'
      }
    })

    visitAchievementsArray[index]['visit_organization_addition'] = Number(visitAchievementsArray[index]['visit_organization_addition'])

    // CSVの復元の際に保訪の実績記録の複数支援ありのkeyが異なるため、統一するための処理
    if (
      visitAchievementsArray[index].is_multiple === '1'
    ) {
      visitAchievementsArray[index]['is_visit_multiple'] = '1'
    }
  }

  return visitAchievementsArray
}

const exchangeKeyArray = [
  { setKey: 'affiliation', dataKey: 'affiliation' },
  { setKey: 'cldId', dataKey: 'cldId' },
  { setKey: 'id', dataKey: 'id' },
  { setKey: 'year', dataKey: 'year' },
  { setKey: 'month', dataKey: 'month' },
  { setKey: 'day', dataKey: 'day' },
  { setKey: 'isVisitMultiple', dataKey: 'isVisitMultiple' },
  { setKey: 'visitFlg', dataKey: 'flg' },
  { setKey: 'visitKasanFirst', dataKey: 'kasanFirst' },
  { setKey: 'visitComeTimeHour', dataKey: 'comeTimeHour' },
  { setKey: 'visitComeTimeMinute', dataKey: 'comeTimeMinute' },
  { setKey: 'visitReturnTimeHour', dataKey: 'returnTimeHour' },
  { setKey: 'visitReturnTimeMinute', dataKey: 'returnTimeMinute' },
  { setKey: 'visitKasanSpecialArea', dataKey: 'kasanSpecialArea' },
  { setKey: 'visitKasanWelfare', dataKey: 'kasanWelfare' },
  { setKey: 'visitManager', dataKey: 'manager' },
  { setKey: 'visitManagerName', dataKey: 'managerName' },
  { setKey: 'visitLeader', dataKey: 'leader' },
  { setKey: 'visitLeaderName', dataKey: 'leaderName' },
  { setKey: 'visitRemarks', dataKey: 'remarks' },
  { setKey: 'visitHomeAddition', dataKey: 'homeAddition' },
  { setKey: 'visitHomeAdditionStartHour', dataKey: 'homeAdditionStartHour' },
  {
    setKey: 'visitHomeAdditionStartMinute',
    dataKey: 'homeAdditionStartMinute',
  },
  { setKey: 'visitHomeAdditionEndHour', dataKey: 'homeAdditionEndHour' },
  { setKey: 'visitHomeAdditionEndMinute', dataKey: 'homeAdditionEndMinute' },
  { setKey: 'visitRemarksText', dataKey: 'visitRemarksText' },
  { setKey: 'visitOrganizationAddition', dataKey: 'visitOrganizationAddition' },
  { setKey: 'visitOrganizationAdditionStartHour', dataKey: 'visitOrganizationAdditionStartHour' },
  { setKey: 'visitOrganizationAdditionStartMinute', dataKey: 'visitOrganizationAdditionStartMinute' },
  { setKey: 'visitOrganizationAdditionEndHour', dataKey: 'visitOrganizationAdditionEndHour' },
  { setKey: 'visitOrganizationAdditionEndMinute', dataKey: 'visitOrganizationAdditionEndMinute' },
  { setKey: 'visitKasanCareNeeds', dataKey: 'visitKasanCareNeeds' },
  { setKey: 'visitKasanHomonShien', dataKey: 'visitKasanHomonShien' },
  { setKey: 'visitKasanKazokuShien', dataKey: 'visitKasanKazokuShien' },
  { setKey: 'visitKasanKazokuShienPlace', dataKey: 'visitKasanKazokuShienPlace' },
  { setKey: 'visitKasanKazokuShienStartHour', dataKey: 'visitKasanKazokuShienStartHour' },
  { setKey: 'visitKasanKazokuShienStartMinute', dataKey: 'visitKasanKazokuShienStartMinute' },
  { setKey: 'visitKasanKazokuShienEndHour', dataKey: 'visitKasanKazokuShienEndHour' },
  { setKey: 'visitKasanKazokuShienEndMinute', dataKey: 'visitKasanKazokuShienEndMinute' },
  { setKey: 'visitKasanKazokuShien1', dataKey: 'visitKasanKazokuShien1' },
  { setKey: 'visitKasanKazokuShien1Place', dataKey: 'visitKasanKazokuShien1Place' },
  { setKey: 'visitKasanKazokuShien1StartHour', dataKey: 'visitKasanKazokuShien1StartHour' },
  { setKey: 'visitKasanKazokuShien1StartMinute', dataKey: 'visitKasanKazokuShien1StartMinute' },
  { setKey: 'visitKasanKazokuShien1EndHour', dataKey: 'visitKasanKazokuShien1EndHour' },
  { setKey: 'visitKasanKazokuShien1EndMinute', dataKey: 'visitKasanKazokuShien1EndMinute' },
  { setKey: 'visitKasanKazokuShien2', dataKey: 'visitKasanKazokuShien2' },
  { setKey: 'visitKasanKazokuShien2Place', dataKey: 'visitKasanKazokuShien2Place' },
  { setKey: 'visitKasanKazokuShien2StartHour', dataKey: 'visitKasanKazokuShien2StartHour' },
  { setKey: 'visitKasanKazokuShien2StartMinute', dataKey: 'visitKasanKazokuShien2StartMinute' },
  { setKey: 'visitKasanKazokuShien2EndHour', dataKey: 'visitKasanKazokuShien2EndHour' },
  { setKey: 'visitKasanKazokuShien2EndMinute', dataKey: 'visitKasanKazokuShien2EndMinute' },
  { setKey: 'visitKasanStrength', dataKey: 'visitKasanStrength' },
  { setKey: 'visitKasanTashugyo', dataKey: 'visitKasanTashugyo' },
]

export const handleUpdateObjShapeVisitAchievements = (
  visitAchievementsObj: cldBookForAchievementsType
) => {
  type cldBookForAchievementsTypeKey = keyof cldBookForAchievementsType
  const visitAchievements: any = {}

  const setVisitAchievementsObj = JSON.parse(
    JSON.stringify(visitAchievementsObj)
  )
  ;(Object.keys(
    setVisitAchievementsObj
  ) as cldBookForAchievementsTypeKey[]).forEach(camelKey => {
    let snakeKey = format.snakeCase(camelKey)

    const targetExchangeKey = exchangeKeyArray.find(
      obj => obj.setKey === camelKey
    )

    if (targetExchangeKey) {
      snakeKey = format.snakeCase(targetExchangeKey.dataKey)

      visitAchievements[snakeKey] = setVisitAchievementsObj[camelKey]
    }
  })

  return handleShapeVisitAchievements([visitAchievements])[0]
}

export const handleShapeVisitAchievementsForCldBookObj = (
  visitAchievements: DisplayVisitAchievementsType
) => {
  if (!visitAchievements) return

  type ResponseVisitAchievementsTypeKey = keyof DisplayVisitAchievementsType

  const cldBookObj: any = {}
  ;(Object.keys(
    visitAchievements
  ) as ResponseVisitAchievementsTypeKey[]).forEach(snakeKey => {
    const camelKey = format.camelCase(snakeKey)

    const targetExchangeKey = exchangeKeyArray.find(
      obj => obj.dataKey === camelKey
    )

    if (targetExchangeKey) {
      const setSnakeKey = format.snakeCase(
        targetExchangeKey.setKey
      ) as ResponseVisitAchievementsTypeKey

      cldBookObj[setSnakeKey] = visitAchievements[snakeKey]
    }
  })

  const remarks = handleSetVisitAchivementsVisitRemarks(visitAchievements)

  // eslint-disable-next-line @typescript-eslint/camelcase
  cldBookObj.visit_remarks = remarks

  return cldBookObj
}

export const handleShapeAchievements = (achievementsArray: any) => {
  for (let index = 0; index < achievementsArray.length; index++) {
    let KeyPrefix = ''

    const taregetTimeKeyReg = new RegExp(
      'StartHour|StartMinute|EndHour|EndMinute'
    )

    let checkKasanKeyArray = defineObject.ACHIVEMENTS_REVISION_2024_KASAN_KEY_ARRAY.filter(
      (kasanKey: string) => !kasanKey.match(taregetTimeKeyReg)
    )

    checkKasanKeyArray = checkKasanKeyArray.map((kasanKey: string) =>
      format.snakeCase(kasanKey)
    )

    const comeAndReturnKeyArray = [
      'come_time_hour',
      'come_time_minute',
      'return_time_hour',
      'return_time_minute',
    ]

    comeAndReturnKeyArray.forEach((key: string) => {
      if (achievementsArray[index][key]) {
        achievementsArray[index][key] = (
          '00' + achievementsArray[index][key]
        ).slice(-2)
      } else {
        achievementsArray[index][key] = '00'
      }
    })

    if (achievementsArray[index].affiliation == '1') {
      KeyPrefix = 'jihatsu_'
    }

    const intKeyPrimitiveKeyArray = [
      'flg',
      'pick',
      'send',
      'year',
      'month',
      'day',
    ]

    intKeyPrimitiveKeyArray.forEach((key: string) => {
      if (
        achievementsArray[index][key] !== undefined &&
        achievementsArray[index][key] !== null &&
        achievementsArray[index][key] !== ''
      ) {
        achievementsArray[index][key] = Number(achievementsArray[index][key])
      }
    })

    const strKeyPrimitiveKeyArray = [`provision_form`]

    strKeyPrimitiveKeyArray.forEach((key: string) => {
      if (
        achievementsArray[index][key] !== undefined &&
        achievementsArray[index][key] !== null &&
        achievementsArray[index][key] !== ''
      ) {
        achievementsArray[index][key] = String(achievementsArray[index][key])
      }
    })

    checkKasanKeyArray.forEach((key: string) => {
      if (
        (achievementsArray[index][KeyPrefix + key] === '0' ||
          achievementsArray[index][KeyPrefix + key] === 0) &&
        (defineObject.ACHIVEMENTS_REVISION_2024_KASAN_TIME_KEY_ARRAY.indexOf(key) !== -1)

      ) {
        achievementsArray[index][KeyPrefix + key + '_start_hour'] = '00'
        achievementsArray[index][KeyPrefix + key + '_start_minute'] = '00'
        achievementsArray[index][KeyPrefix + key + '_end_hour'] = '00'
        achievementsArray[index][KeyPrefix + key + '_end_minute'] = '00'
      }

      if (
        key === 'organization_addition' ||
        key === 'organization_addition2' ||
        key === 'organization_addition3' ||
        key === 'organization_addition4' ||
        key === 'consultation_addition' ||
        key === 'consultation_addition2'
      ) {
        achievementsArray[index][KeyPrefix + key] = Number(
          achievementsArray[index][KeyPrefix + key]
        )
      }
    })

    const diff = calculationOfUsageTime(
      achievementsArray[index].come_time_hour,
      achievementsArray[index].come_time_minute,
      achievementsArray[index].return_time_hour,
      achievementsArray[index].return_time_minute
    )

    if (
      achievementsArray[index].affiliation === '0' &&
      achievementsArray[index].alternative_support !== '1' &&
      diff > 0 &&
      diff <= 30
    ) {
      // 欠席加算Ⅱの場合はエラーにならないようにダミーの提供形態をセット
      achievementsArray[index]['provision_form'] =
        achievementsArray[index]['provision_form'] === '' ||
        achievementsArray[index]['provision_form'] === undefined
          ? '0'
          : achievementsArray[index]['provision_form']
    }
  }

  return achievementsArray
}

const handleSetKasanKazokuShienSnakeCase = (
  achievementsArray: any[]
) => {
  const keyMapping = {
    '1': '1',
    '2': '1',
    '3': '1',
    '4': '1',
    '5': '2',
    '6': '2',
  } as any
  const key = 'kasan_kazoku_shien'
  achievementsArray.forEach(el => {
    let KeyPrefix = ''
    if (el.affiliation === '1') {
      KeyPrefix = 'jihatsu_'
    }
    if (el.affiliation === '2') {
      KeyPrefix = 'visit_'
    }
    const snakeKey = `${KeyPrefix}${key}`
    const group = keyMapping[el[snakeKey]]

    if (group) {
      const suffix = group
      const prefix = snakeKey + group

      el[prefix] = '1'
      el[prefix + '_start_hour'] =
      el[snakeKey + suffix + '_start_hour'] ?? el[snakeKey + '_start_hour']
      el[prefix + '_start_minute'] =
      el[snakeKey + suffix + '_start_minute'] ?? el[snakeKey + '_start_minute']
        el[prefix + '_end_hour'] =
        el[snakeKey + suffix + '_end_hour'] ?? el[snakeKey + '_end_hour']
        el[prefix + '_end_minute'] =
        el[snakeKey + suffix + '_end_minute'] ?? el[snakeKey + '_end_minute']
        el[prefix + '_place'] =
        el[snakeKey + suffix + '_place'] ?? el[snakeKey + '_place']
    }
  })
}

export const handleIsIncompleteAchievementsCheck = (
  achievementsArray: { [key: string]: any }[],
  cldObj: any
) => {
  handleSetKasanKazokuShienSnakeCase(achievementsArray)
  achievementsArray = JSON.parse(JSON.stringify(achievementsArray))

  achievementsArray.sort(
    (firstData, secondData) => firstData.day - secondData.day
  )

  const errorMsgArray: {
    errMsg: string
    year: number
    month: number
    day: number
  }[] = []

  let KeyPrefix = ''

  const taregetTimeKeyReg = new RegExp(
    'StartHour|StartMinute|EndHour|EndMinute'
  )

  let checkKasanKeyArray = defineObject.ACHIVEMENTS_REVISION_2024_KASAN_KEY_ARRAY.filter(
    (kasanKey: string) => !kasanKey.match(taregetTimeKeyReg)
  )

  checkKasanKeyArray = checkKasanKeyArray.map((kasanKey: string) =>
    format.snakeCase(kasanKey)
  )

  try {
    if (achievementsArray.length === 0) throw new Error('')

    if (achievementsArray[0].affiliation === '1') {
      KeyPrefix = 'jihatsu_'
    }

    for (let index = 0; index < achievementsArray.length; index++) {
      const targetAchievementsArray = checkKasanKeyArray.filter(
        (checkKasanKey: string) =>
        ( checkKasanKey !== 'kasan_kazoku_shien1' && checkKasanKey !== 'kasan_kazoku_shien2' && checkKasanKey !== 'kasan_kazoku_shien' &&
          (achievementsArray[index][KeyPrefix + checkKasanKey] === '1' ||
            achievementsArray[index][KeyPrefix + checkKasanKey] === 1) &&
          calculationOfUsageTime(
            achievementsArray[index][KeyPrefix + checkKasanKey + '_start_hour'],
            achievementsArray[index][
            KeyPrefix + checkKasanKey + '_start_minute'
            ],
            achievementsArray[index][KeyPrefix + checkKasanKey + '_end_hour'],
            achievementsArray[index][KeyPrefix + checkKasanKey + '_end_minute']
            ) <= 0 &&
            checkKasanKey === 'kasan_'
          )
          ||
          (
            ((checkKasanKey === 'kasan_kazoku_shien1' || checkKasanKey === 'kasan_kazoku_shien2') && achievementsArray[index][KeyPrefix + checkKasanKey] === '1') &&
          achievementsArray[index][KeyPrefix + checkKasanKey] === '1' &&
          calculationOfUsageTime(
            achievementsArray[index][KeyPrefix + checkKasanKey + '_start_hour'] ? achievementsArray[index][KeyPrefix + checkKasanKey + '_start_hour'] : '00',
            achievementsArray[index][
            KeyPrefix + checkKasanKey + '_start_minute'
            ] ? achievementsArray[index][KeyPrefix + checkKasanKey + '_start_minute'] : '00',
            achievementsArray[index][KeyPrefix + checkKasanKey + '_end_hour'] ? achievementsArray[index][KeyPrefix + checkKasanKey + '_end_hour'] : '00',
            achievementsArray[index][KeyPrefix + checkKasanKey + '_end_minute'] ? achievementsArray[index][KeyPrefix + checkKasanKey + '_end_minute'] : '00'
            ) <= 0)
      )

      const kasanObjArray = []

      for (let index = 0; index < targetAchievementsArray.length; index++) {
        const kasanObj = defineObject.ACHIVEMENTS_REVISION_2024_KASAN_KEY_TEXT_ARRAY.find(
          (checkKasanObj: any) =>
            checkKasanObj.key === targetAchievementsArray[index]
        )

        kasanObjArray.push(kasanObj?.text)
      }

      if (kasanObjArray.length !== 0) {
        errorMsgArray.push({
          year: Number(achievementsArray[index].year),
          month: Number(achievementsArray[index].month),
          day: Number(achievementsArray[index].day),
          errMsg: `${handleCurrentJapaneseCalendar(
            achievementsArray[index].year,
            Number(achievementsArray[index].month)
          )}${achievementsArray[index].day}日の実績の${kasanObjArray.join(
            ','
          )}の実施時間を修正してください。`,
        })
      }
    }

    for (let index = 0; index < achievementsArray.length; index++) {
      const targetAchievementsPlaceArray = checkKasanKeyArray.filter(
        (checkKasanKey: string) =>
          (((checkKasanKey === 'kasan_kazoku_shien1' || checkKasanKey === 'kasan_kazoku_shien2') && achievementsArray[index][KeyPrefix + checkKasanKey] === '1') &&
          achievementsArray[index][KeyPrefix + checkKasanKey] === '1' &&
          (achievementsArray[index][KeyPrefix + checkKasanKey + '_place'] === '0' || achievementsArray[index][KeyPrefix + checkKasanKey + '_place'] === undefined)
          )
      )
      const kasanObjArray = []

      for (let index = 0; index < targetAchievementsPlaceArray.length; index++) {
        const kasanObj = defineObject.ACHIVEMENTS_REVISION_2024_KASAN_KEY_TEXT_ARRAY.find(
          (checkKasanObj: any) =>
            checkKasanObj.key === targetAchievementsPlaceArray[index]
        )

        kasanObjArray.push(kasanObj?.text)
      }

      if (kasanObjArray.length !== 0) {
        errorMsgArray.push({
          year: Number(achievementsArray[index].year),
          month: Number(achievementsArray[index].month),
          day: Number(achievementsArray[index].day),
          errMsg: `${handleCurrentJapaneseCalendar(
            achievementsArray[index].year,
            Number(achievementsArray[index].month)
          )}${achievementsArray[index].day}日の実績の${kasanObjArray.join(
            ','
          )}の実施場所を修正してください。`,
        })
      }
    }

    const achievementsFilterArray = achievementsArray.filter(
      (achievements: any) =>
        achievements.flg === 1 &&
        calculationOfUsageTime(
          achievements['come_time_hour'],
          achievements['come_time_minute'],
          achievements['return_time_hour'],
          achievements['return_time_minute']
        ) <= 0
    )

    for (let index = 0; index < achievementsFilterArray.length; index++) {
      errorMsgArray.push({
        year: Number(achievementsFilterArray[index].year),
        month: Number(achievementsFilterArray[index].month),
        day: Number(achievementsFilterArray[index].day),
        errMsg: `${handleCurrentJapaneseCalendar(
          achievementsFilterArray[index].year,
          Number(achievementsFilterArray[index].month)
        )}${
          achievementsFilterArray[index].day
        }日の開始と終了の時間を修正してください。`,
      })
    }

    if (achievementsArray[0].affiliation === '0') {
      const achievementsHoudayFilterArray = achievementsArray.filter(
        (achievements: any) =>
          achievements.flg === 1 &&
          (achievements['provision_form'] === undefined ||
            achievements['provision_form'] === null ||
            achievements['provision_form'] === '')
      )

      for (
        let index = 0;
        index < achievementsHoudayFilterArray.length;
        index++
      ) {
        errorMsgArray.push({
          year: Number(achievementsHoudayFilterArray[index].year),
          month: Number(achievementsHoudayFilterArray[index].month),
          day: Number(achievementsHoudayFilterArray[index].day),
          errMsg: `${handleCurrentJapaneseCalendar(
            achievementsHoudayFilterArray[index].year,
            Number(achievementsHoudayFilterArray[index].month)
          )}${
            achievementsHoudayFilterArray[index].day
          }日の実績の提供形態を修正してください。`,
        })
      }
    }

    // handleCheckCountConsultationAddition(achievementsArray, errorMsgArray)

    handleCheckCountOrganizationAddition(achievementsArray, errorMsgArray)
    handleCheckKasanNyuyoku(achievementsArray, errorMsgArray)
    handleCheckKasanKazokuShien(achievementsArray, errorMsgArray)
    handleCheckKasanKosodateSupport(achievementsArray, errorMsgArray)
    handleCheckKasanSenmontekiJisshi(achievementsArray, errorMsgArray)
    handleCheckKasanJigyoshokan(achievementsArray, errorMsgArray)
    handleCheckKasanJiritsuSupport(achievementsArray, errorMsgArray)
    handleCheckKasanIkoShien(achievementsArray, errorMsgArray)
    handleCheckKasanShuchu(achievementsArray, errorMsgArray, cldObj, cldObj)
    handleCheckCalculationTimeOverOfProvisionFormOpen(achievementsArray, errorMsgArray, cldObj, cldObj)
    handleCheckCalculationTimeOverOfProvisionFormClosed(achievementsArray, errorMsgArray, cldObj, cldObj)
    handleCheckAbsence(achievementsArray, errorMsgArray)

    handleCheckDiffCldObjAffiliationAndAchievementsAffiliation(
      achievementsArray as ResponseAchievementsType[],
      cldObj,
      errorMsgArray,
      false,
      true
    )

    return errorMsgArray
      .slice()
      .sort(
        (a, b) =>
          new Date(`${a.year}/${a.month}/${a.day}`).getTime() -
          new Date(`${b.year}/${b.month}/${b.day}`).getTime()
      )
  } catch (error) {
    return errorMsgArray
  }
}

export const handleIsIncompleteVisitAchievementsCheck = (
  visitAchievementsArray: { [key: string]: any }[],
  cldObj: any
) => {
  handleSetKasanKazokuShienSnakeCase(visitAchievementsArray)
  visitAchievementsArray = JSON.parse(JSON.stringify(visitAchievementsArray))

  visitAchievementsArray.sort(
    (firstData, secondData) => firstData.day - secondData.day
  )

  const errorMsgArray: {
    errMsg: string
    year: number
    month: number
    day: number
  }[] = []

  const taregetTimeKeyReg = new RegExp(
    'StartHour|StartMinute|EndHour|EndMinute'
  )

  let checkKasanKeyArray = defineObject.ACHIVEMENTS_REVISION_2024_KASAN_KEY_ARRAY.filter(
    (kasanKey: string) =>
      !kasanKey.match(taregetTimeKeyReg) && (kasanKey === 'organizationAddition' || kasanKey === 'kasanKazokuShien1' || kasanKey === 'kasanKazokuShien2')
  )

  checkKasanKeyArray = checkKasanKeyArray.map((kasanKey: string) =>
    format.snakeCase(kasanKey)
  )

  for (let index = 0; index < visitAchievementsArray.length; index++) {
    const targetVisitAchievementsArray = checkKasanKeyArray.filter(
      (checkKasanKey: string) =>
        (checkKasanKey !== 'kasan_kazoku_shien1' && checkKasanKey !== 'kasan_kazoku_shien2' && checkKasanKey !== 'kasan_kazoku_shien' &&
          (visitAchievementsArray[index]['visit_' + checkKasanKey] === '1' ||
          visitAchievementsArray[index]['visit_' + checkKasanKey] === 1) &&
        calculationOfUsageTime(
          visitAchievementsArray[index]['visit_' + checkKasanKey + '_start_hour'] ? visitAchievementsArray[index]['visit_' + checkKasanKey + '_start_hour'] : '00',
          visitAchievementsArray[index]['visit_' + checkKasanKey + '_start_minute'] ? visitAchievementsArray[index]['visit_' + checkKasanKey + '_start_minute'] : '00',
          visitAchievementsArray[index]['visit_' + checkKasanKey + '_end_hour'] ? visitAchievementsArray[index]['visit_' + checkKasanKey + '_end_hour'] : '00',
          visitAchievementsArray[index]['visit_' + checkKasanKey + '_end_minute'] ? visitAchievementsArray[index]['visit_' + checkKasanKey + '_end_minute'] : '00',
          ) <= 0) ||
        ((checkKasanKey === 'kasan_kazoku_shien1' || checkKasanKey === 'kasan_kazoku_shien2') &&
        visitAchievementsArray[index]['visit_' + checkKasanKey] === '1' &&
        calculationOfUsageTime(
          visitAchievementsArray[index]['visit_' + checkKasanKey + '_start_hour'] ? visitAchievementsArray[index]['visit_' + checkKasanKey + '_start_hour'] : '00',
          visitAchievementsArray[index][
          'visit_' + checkKasanKey + '_start_minute'
          ] ? visitAchievementsArray[index]['visit_' + checkKasanKey + '_start_minute'] : '00',
          visitAchievementsArray[index]['visit_' + checkKasanKey + '_end_hour'] ? visitAchievementsArray[index]['visit_' + checkKasanKey + '_end_hour'] : '00',
          visitAchievementsArray[index]['visit_' + checkKasanKey + '_end_minute'] ? visitAchievementsArray[index]['visit_' + checkKasanKey + '_end_minute'] : '00'
        ) <= 0)
    )

    const kasanObjArray = []

    for (let index = 0; index < targetVisitAchievementsArray.length; index++) {
      const kasanObj = defineObject.ACHIVEMENTS_REVISION_2024_KASAN_KEY_TEXT_ARRAY.find(
        (checkKasanObj: any) =>
          checkKasanObj.key === targetVisitAchievementsArray[index]
      )

      kasanObjArray.push(kasanObj?.text)
    }

    if (kasanObjArray.length !== 0) {
      errorMsgArray.push({
        year: Number(visitAchievementsArray[index].year),
        month: Number(visitAchievementsArray[index].month),
        day: Number(visitAchievementsArray[index].day),
        errMsg: `${handleCurrentJapaneseCalendar(
          visitAchievementsArray[index].year,
          Number(visitAchievementsArray[index].month)
        )}${visitAchievementsArray[index].day}日の実績の${kasanObjArray.join(
          ','
        )}の実施時間を修正してください。`,
      })
    }
  }

  for (let index = 0; index < visitAchievementsArray.length; index++) {
    const targetAchievementsPlaceArray = checkKasanKeyArray.filter(
      (checkKasanKey: string) =>
        (((checkKasanKey === 'kasan_kazoku_shien1' || checkKasanKey === 'kasan_kazoku_shien2') && visitAchievementsArray[index]['visit_' + checkKasanKey] === '1') &&
      visitAchievementsArray[index]['visit_' + checkKasanKey] === '1' &&
        (visitAchievementsArray[index]['visit_' + checkKasanKey + '_place'] === '0' || visitAchievementsArray[index]['visit_' + checkKasanKey + '_place'] === undefined)
        )
    )
    const kasanObjArray = []

    for (let index = 0; index < targetAchievementsPlaceArray.length; index++) {
      const kasanObj = defineObject.ACHIVEMENTS_REVISION_2024_KASAN_KEY_TEXT_ARRAY.find(
        (checkKasanObj: any) =>
          checkKasanObj.key === targetAchievementsPlaceArray[index]
      )

      kasanObjArray.push(kasanObj?.text)
    }

    if (kasanObjArray.length !== 0) {
      errorMsgArray.push({
        year: Number(visitAchievementsArray[index].year),
        month: Number(visitAchievementsArray[index].month),
        day: Number(visitAchievementsArray[index].day),
        errMsg: `${handleCurrentJapaneseCalendar(
          visitAchievementsArray[index].year,
          Number(visitAchievementsArray[index].month)
        )}${visitAchievementsArray[index].day}日の実績の${kasanObjArray.join(
          ','
        )}の実施場所を修正してください。`,
      })
    }
  }

  const visitAchievementsFilterArray = visitAchievementsArray.filter(
    (visitAchievements: any) =>
      visitAchievements.flg === 1 &&
      calculationOfUsageTime(
        visitAchievements['come_time_hour'],
        visitAchievements['come_time_minute'],
        visitAchievements['return_time_hour'],
        visitAchievements['return_time_minute']
      ) <= 0
  )

  for (let index = 0; index < visitAchievementsFilterArray.length; index++) {
    errorMsgArray.push({
      year: Number(visitAchievementsFilterArray[index].year),
      month: Number(visitAchievementsFilterArray[index].month),
      day: Number(visitAchievementsFilterArray[index].day),
      errMsg: `${handleCurrentJapaneseCalendar(
        visitAchievementsFilterArray[index].year,
        Number(visitAchievementsFilterArray[index].month)
      )}${
        visitAchievementsFilterArray[index].day
      }日の開始と終了の時間を修正してください。`,
    })
  }

  handleCheckVisitOrganizationAddition(visitAchievementsArray, errorMsgArray)
  handleCheckVisitKasanKazokuShien(visitAchievementsArray, errorMsgArray)
  handleCheckVisitKasanTashugyo(visitAchievementsArray, errorMsgArray)


  handleCheckDupDayAchivementsArray(visitAchievementsArray, errorMsgArray)

  if (!cldObj.visitChildFlg) {
    cldObj.visitChildFlg = cldObj.visit_child_flg
  }

  handleCheckDiffCldObjAffiliationAndAchievementsAffiliationByVisit(
    visitAchievementsArray as ResponseVisitAchievementsType[],
    cldObj,
    errorMsgArray,
    false,
    true
  )

  try {
    if (visitAchievementsArray.length === 0) throw new Error('')

    return errorMsgArray
      .slice()
      .sort(
        (a, b) =>
          new Date(`${a.year}/${a.month}/${a.day}`).getTime() -
          new Date(`${b.year}/${b.month}/${b.day}`).getTime()
      )
  } catch (error) {
    return errorMsgArray
  }
}

export const handleIsUpdateDitails = (cldId: string, state: any) => {
  try {
    const officeErrDataArray = handleCheckBillingOfficeDataForRevision2024(
      state.reBillingData.office,
      true
    )
    console.log(officeErrDataArray)

    if (officeErrDataArray.length !== 0) throw new Error('事業所不備あり')

    const cldObj = state.reBillingData.child.find(
      (cldObj: any) => cldObj.id === cldId
    )

    const isChildUpdated = handleIsChildCheck(
      cldObj,
      state.reBillingData.office
    )

    if (!isChildUpdated) throw new Error('児童情報に不備あり')

    const achievementsArray = state.reBillingData.achievements.filter(
      (achievements: any) => achievements.cld_id === cldId
    )

    const errorMsgArray = handleIsIncompleteAchievementsCheck(
      achievementsArray,
      cldObj
    )

    // 加算がある実績で時間を持っていないデータ不備の実績のチェック
    if (errorMsgArray.length !== 0) throw new Error('実績記録に不備あり')

    return true
  } catch (error) {
    console.log(error)
    return false
  }
}

export const handleCurrentJapaneseCalendarYearOnly = (
  year: number | Date,
  month: number | Date
) => {
  const currentDate = new Date(Number(year), Number(month) - 1)
  const options: { [key: string]: string } = { year: 'numeric' }
  const japaneseDate = new Intl.DateTimeFormat(
    'ja-JP-u-ca-japanese',
    options
  ).format(currentDate)
  return `${japaneseDate}`
}

export const handlePreSetParentsInvoice = (
  cldId: string,
  affiliation: string,
  visitChildFlg: string
) => {
  const office = getSessionStorageOfficeData()
  const child = getSessionStorageChildDataByCldId(cldId)
  const reBillingData = getSessionStoragReBillingData()

  const reBellingTargetDate = getSessionStorageReBillingDate()

  const setKeyArray = [
    'child',
    'office',
    'dateNowW',
    'dateNowY',
    'dateNowM',
    'dateNowD',
    'invoiceNo',
    'billingDateNowW',
    'billingDateNowY',
    'billingDateNowM',
    'date',
    'receiptDate',
    'useDay',
    'affiliation',
    'inputSelectNowDate',
    'inputPrevNowDate',
    'atCost',
    'atCostSummary',
  ]

  if (!reBillingData.invoice) return

  const targetIndex = reBillingData.invoice.findIndex(
    (cldInvoice: any) =>
      cldInvoice.select &&
      cldInvoice.select.child &&
      cldInvoice.select.child.cld_id === cldId
  )

  let billing: any = {
    details: {
      master: {},
    },
  }

  if (getSessionStoragCldReBillingDataByCld(cldId)) {
    billing = getSessionStoragCldReBillingDataByCld(cldId)
  }

  if (!reBillingData.invoice[targetIndex]) return

  const invoice = reBillingData.invoice[targetIndex]

  const dateArray: string[] | null = String(
    handleCurrentJapaneseCalendar(
      Number(reBellingTargetDate.year),
      Number(reBellingTargetDate.month)
    )
  ).match(/.{2}/g)

  const nowDateArray: string[] | null = handleJapaneseCalendar(
    new Date()
  ).match(/.{2}/g)

  const nowMonth = moment(new Date()).month() + 1

  const nowDate = moment(new Date()).date()

  setKeyArray.forEach((setKey: string) => {
    switch (setKey) {
      case 'child':
        {
          const shapeChild = handleShapeUpdateReBillingCldParam(child)

          shapeChild.basicSurvey = {
            basicSurvey1: {
              zip: shapeChild.zip ? shapeChild.zip : '',
              prefecture: shapeChild.prefecture ? shapeChild.prefecture : '',
              city: shapeChild.city ? shapeChild.city : '',
              block: shapeChild.block ? shapeChild.block : '',
              building: shapeChild.building ? shapeChild.building : '',
            },
          }

          shapeChild.basicSurvey = JSON.stringify(shapeChild.basicSurvey)

          if (!shapeChild.cld_id) shapeChild['cld_id'] = shapeChild.id

          invoice.select[setKey] = shapeChild
        }
        break

      case 'office':
        invoice.select[setKey] = handleShapeUpdateReBillingOfficeParam(office)
        break

      case 'dateNowW':
        invoice.select[setKey] = nowDateArray ? nowDateArray[0] : ''
        break

      case 'dateNowY':
        invoice.select[setKey] = nowDateArray
          ? String(parseInt(nowDateArray[1]))
          : ''
        break

      case 'dateNowM':
        invoice.select[setKey] = nowMonth ? String(nowMonth) : ''
        break

      case 'dateNowD':
        invoice.select[setKey] = nowDate ? String(nowDate) : ''
        break

      case 'billingDateNowW':
        invoice.select[setKey] = dateArray ? dateArray[0] : ''
        break

      case 'billingDateNowY':
        invoice.select[setKey] = dateArray ? String(parseInt(dateArray[1])) : ''
        break

      case 'billingDateNowM':
        invoice.select[setKey] = dateArray ? String(parseInt(dateArray[2])) : ''
        break

      case 'inputSelectNowDate':
      case 'inputPrevNowDate':
        invoice.select[setKey] = invoice.select[setKey]
          ? invoice.select[setKey]
          : `${nowDateArray ? nowDateArray[0] : ''}${String(
              parseInt(nowDateArray ? nowDateArray[1] : '')
            )}年${String(nowMonth)}月${String(nowDate)}日`

        console.log(invoice.select[setKey])
        break

      case 'invoiceNo':
        invoice.select[setKey] = invoice.select[setKey]
          ? invoice.select[setKey]
          : `${String(reBellingTargetDate.year).slice(2)}${String(
              reBellingTargetDate.month
            )}-${child.beneficiary_id}`
        break

      case 'date':
        invoice[
          setKey
        ] = `${reBellingTargetDate.year}/${reBellingTargetDate.month}`
        break

      case 'receiptDate':
        invoice.select[setKey] = invoice.select[setKey]
          ? invoice.select[setKey]
          : ''
        break

      case 'atCost':
        invoice.select[setKey] = invoice.select[setKey]
          ? invoice.select[setKey]
          : []
        ''
        break

      case 'atCostSummary':
        invoice.select[setKey] = invoice.select[setKey]
          ? invoice.select[setKey]
          : 0
        break

      case 'useDay':
        invoice.select[setKey] =
          billing.details.master.summary && billing.details.master.summary[0]
            ? billing.details.master.summary[0].useDay
            : 0
        break

      case 'affiliation':
        invoice.select[setKey] = billing.affiliation ? billing.affiliation : ''
        break
    }
  })

  handleSetInvoicePrice(billing.details.master, invoice.select)

  if (reBillingData.invoice[targetIndex]) {
    reBillingData.invoice.splice(targetIndex, 1, invoice)
  }

  sessionStrageUtils.handleSetSessionStorage('reBillingData', reBillingData)

  return invoice
}

export const handleGetFilterChildInfoByRebillingTarget = () => {
  let childInfoArray = getSessionStorageChildData()
  childInfoArray = childInfoArray.filter(
    (cldInfo: any) => cldInfo.billing_target
  )

  return childInfoArray
}

export const handleGetFilterChildAchivementsByRebillingTarget = () => {
  const childInfoArray = handleGetFilterChildInfoByRebillingTarget()
  let childAchivementsArray = getSessionStorageAchievementsData()

  childAchivementsArray = childAchivementsArray.filter((achievements: any) =>
    childInfoArray.some((cldInfo: any) => cldInfo.id === achievements.cld_id)
  )

  return childAchivementsArray
}

export const handleGetFilterChildVisitAchivementsByRebillingTarget = () => {
  const childInfoArray = handleGetFilterChildInfoByRebillingTarget()
  let childVisitAchivementsArray = getSessionStorageVisitAchievementsData()

  childVisitAchivementsArray = childVisitAchivementsArray.filter(
    (achievements: any) =>
      childInfoArray.some((cldInfo: any) => cldInfo.id === achievements.cld_id)
  )

  return childVisitAchivementsArray
}

export const handleGetFilterChildBillingByRebillingTarget = () => {
  const childInfoArray = handleGetFilterChildInfoByRebillingTarget()
  let childBillingArray = getSessionStoragCldReBillingData()

  childBillingArray = childBillingArray.filter((billingObj: any) =>
    childInfoArray.some((cldInfo: any) => cldInfo.id === billingObj.cld_id)
  )

  return childBillingArray
}

export const handleGetKasanKazokuShienToAchievementsData = (
  props: any,
  state: any
) => {
  let key = 'kasanKazokuShien'
  let snakeKey = 'kasan_kazoku_shien'
  let placeKey = 'kasanKazokuShienPlace'
  const targetAffiliationObj = defineObject.AFFILIATION_ARRAY.find(
    (data: { [key: string]: string }) => data.value === props.targetAffiliation
  )
  console.log(targetAffiliationObj)

  if (targetAffiliationObj) {

    switch (targetAffiliationObj.value) {
      case JIHATSU:
        key = 'jihatsuKasanKazokuShien'
        snakeKey = 'jihatsu_kasan_kazoku_shien'
        placeKey = 'jihatsuKasanKazokuShienPlace'
        break

      case HOHOU:
        key = 'visitKasanKazokuShien'
        snakeKey = 'visit_kasan_kazoku_shien'
        placeKey = 'visitKasanKazokuShienPlace'
        break
    }
    if (state.noteParam.dataObject[key] === '1' || state.noteParam.dataObject[key] === '2') {
      state.noteParam.dataObject[key] = '1'
      state.noteParam.dataObject[placeKey] = '1'
    } else if (state.noteParam.dataObject[key] === '3') {
      state.noteParam.dataObject[key] = '1'
      state.noteParam.dataObject[placeKey] = '2'
    } else if (state.noteParam.dataObject[key] === '4') {
      state.noteParam.dataObject[key] = '1'
      state.noteParam.dataObject[placeKey] = '3'
    } else if (state.noteParam.dataObject[key] === '5') {
      state.noteParam.dataObject[key] = '2'
      state.noteParam.dataObject[placeKey] = '2'
    } else if (state.noteParam.dataObject[key] === '6') {
      state.noteParam.dataObject[key] = '2'
      state.noteParam.dataObject[placeKey] = '3'
    }
  }
}

export const handleGetKasanKazokuShienToCldData = (
  state: any
) => {
  if (
    `${state.noteParam.dataObject[state.keyPrefix + state.kasanKeyObj.kasanKazokuShien1]}` === '1'
  ) {
    state.noteParam.dataObject[state.keyPrefix + state.kasanKeyObj.kasanKazokuShien] = '1'
    state.noteParam.dataObject[state.keyPrefix + state.kasanKeyObj.kasanKazokuShienPlace] = state.noteParam.dataObject[state.keyPrefix + state.kasanKeyObj.kasanKazokuShien1Place]
    state.noteParam.dataObject[state.keyPrefix + state.kasanKeyObj.kasanKazokuShienStartHour] = state.noteParam.dataObject[state.keyPrefix + state.kasanKeyObj.kasanKazokuShien1StartHour]
    state.noteParam.dataObject[state.keyPrefix + state.kasanKeyObj.kasanKazokuShienStartMinute] = state.noteParam.dataObject[state.keyPrefix + state.kasanKeyObj.kasanKazokuShien1StartMinute]
    state.noteParam.dataObject[state.keyPrefix + state.kasanKeyObj.kasanKazokuShienEndHour] = state.noteParam.dataObject[state.keyPrefix + state.kasanKeyObj.kasanKazokuShien1EndHour]
    state.noteParam.dataObject[state.keyPrefix + state.kasanKeyObj.kasanKazokuShienEndMinute] = state.noteParam.dataObject[state.keyPrefix + state.kasanKeyObj.kasanKazokuShien1EndMinute]
  } else if (
    `${state.noteParam.dataObject[state.keyPrefix + state.kasanKeyObj.kasanKazokuShien2]}` === '1'
  ) {
    state.noteParam.dataObject[state.keyPrefix + state.kasanKeyObj.kasanKazokuShien] = '2'
    state.noteParam.dataObject[state.keyPrefix + state.kasanKeyObj.kasanKazokuShienPlace] = state.noteParam.dataObject[state.keyPrefix + state.kasanKeyObj.kasanKazokuShien2Place]
    state.noteParam.dataObject[state.keyPrefix + state.kasanKeyObj.kasanKazokuShienStartHour] = state.noteParam.dataObject[state.keyPrefix + state.kasanKeyObj.kasanKazokuShien2StartHour]
    state.noteParam.dataObject[state.keyPrefix + state.kasanKeyObj.kasanKazokuShienStartMinute] = state.noteParam.dataObject[state.keyPrefix + state.kasanKeyObj.kasanKazokuShien2StartMinute]
    state.noteParam.dataObject[state.keyPrefix + state.kasanKeyObj.kasanKazokuShienEndHour] = state.noteParam.dataObject[state.keyPrefix + state.kasanKeyObj.kasanKazokuShien2EndHour]
    state.noteParam.dataObject[state.keyPrefix + state.kasanKeyObj.kasanKazokuShienEndMinute] = state.noteParam.dataObject[state.keyPrefix + state.kasanKeyObj.kasanKazokuShien2EndMinute]
  } else {
    state.noteParam.dataObject[state.keyPrefix + state.kasanKeyObj.kasanKazokuShien] = '0'
    state.noteParam.dataObject[state.keyPrefix + state.kasanKeyObj.kasanKazokuShienPlace] = '0'
    state.noteParam.dataObject[state.keyPrefix + state.kasanKeyObj.kasanKazokuShienStartHour] = '00'
    state.noteParam.dataObject[state.keyPrefix + state.kasanKeyObj.kasanKazokuShienStartMinute] = '00'
    state.noteParam.dataObject[state.keyPrefix + state.kasanKeyObj.kasanKazokuShienEndHour] = '00'
    state.noteParam.dataObject[state.keyPrefix + state.kasanKeyObj.kasanKazokuShienEndMinute] = '00'
  }
  if (
    `${state.noteParam.dataObject[state.keyPrefix + state.kasanKeyObj.kasanKazokuShien]}` === '1' &&
    `${state.noteParam.dataObject[state.keyPrefix + state.kasanKeyObj.kasanKazokuShienPlace]}` === '1' &&
    calculationOfUsageTime(
      state.noteParam.dataObject[state.keyPrefix + state.kasanKeyObj.kasanKazokuShienStartHour],
      state.noteParam.dataObject[state.keyPrefix + state.kasanKeyObj.kasanKazokuShienStartMinute],
      state.noteParam.dataObject[state.keyPrefix + state.kasanKeyObj.kasanKazokuShienEndHour],
      state.noteParam.dataObject[state.keyPrefix + state.kasanKeyObj.kasanKazokuShienEndMinute],
    ) >= 60
  ) {
    state.noteParam.dataObject[state.keyPrefix + state.kasanKeyObj.kasanKazokuShien] = '1'
  } else if (
    `${state.noteParam.dataObject[state.keyPrefix + state.kasanKeyObj.kasanKazokuShien]}` === '1' &&
    `${state.noteParam.dataObject[state.keyPrefix + state.kasanKeyObj.kasanKazokuShienPlace]}` === '1' &&
    calculationOfUsageTime(
      state.noteParam.dataObject[state.keyPrefix + state.kasanKeyObj.kasanKazokuShienStartHour],
      state.noteParam.dataObject[state.keyPrefix + state.kasanKeyObj.kasanKazokuShienStartMinute],
      state.noteParam.dataObject[state.keyPrefix + state.kasanKeyObj.kasanKazokuShienEndHour],
      state.noteParam.dataObject[state.keyPrefix + state.kasanKeyObj.kasanKazokuShienEndMinute],
    ) < 60
  ) {
    state.noteParam.dataObject[state.keyPrefix + state.kasanKeyObj.kasanKazokuShien] = '2'
  } else if (
    `${state.noteParam.dataObject[state.keyPrefix + state.kasanKeyObj.kasanKazokuShien]}` === '1' &&
    `${state.noteParam.dataObject[state.keyPrefix + state.kasanKeyObj.kasanKazokuShienPlace]}` === '2'
  ) {
    state.noteParam.dataObject[state.keyPrefix + state.kasanKeyObj.kasanKazokuShien] = '3'
  } else if (
    `${state.noteParam.dataObject[state.keyPrefix + state.kasanKeyObj.kasanKazokuShien]}` === '1' &&
    `${state.noteParam.dataObject[state.keyPrefix + state.kasanKeyObj.kasanKazokuShienPlace]}` === '3'
  ) {
    state.noteParam.dataObject[state.keyPrefix + state.kasanKeyObj.kasanKazokuShien] = '4'
  } else if (
    `${state.noteParam.dataObject[state.keyPrefix + state.kasanKeyObj.kasanKazokuShien]}` === '2' &&
    `${state.noteParam.dataObject[state.keyPrefix + state.kasanKeyObj.kasanKazokuShienPlace]}` === '2'
  ) {
    state.noteParam.dataObject[state.keyPrefix + state.kasanKeyObj.kasanKazokuShien] = '5'
  } else if (
    `${state.noteParam.dataObject[state.keyPrefix + state.kasanKeyObj.kasanKazokuShien]}` === '2' &&
    `${state.noteParam.dataObject[state.keyPrefix + state.kasanKeyObj.kasanKazokuShienPlace]}` === '3'
  ) {
    state.noteParam.dataObject[state.keyPrefix + state.kasanKeyObj.kasanKazokuShien] = '6'
  }
}
